import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import {
	AgentRowAvatar,
	type PresenceHexOutline,
} from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import { useAgentAvatarColorMode } from '@confluence/agent-user-dual-avatar/entry-points/useAgentAvatarColorMode.ts';
import { getAgentBrand } from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { UserShape } from '../ownership-models.ts';

import { useCollaboratorsQueryResult } from './CollaboratorsQueryProvider.tsx';
import { getCollaboratorItems } from './getCollaboratorItems.ts';
import { getUserAvatarSrc } from './getUserAvatarSrc.ts';

const MAX_NUMBERED_OTHER_CONTRIBUTORS = 4;
const MAX_TOTAL_BYLINE_AVATARS = 3;
// Matches the 2px surface ring ADS Avatar draws, so brand tiles separate from overlapping avatars.
const BYLINE_PRESENCE_OUTLINE: PresenceHexOutline = { width: 2 };
const BYLINE_AGENT_PRESENCE_ICON_DIMENSIONS = { width: 16, height: 16 };

const messages = defineMessages({
	byline: {
		id: 'content-ownership.byline.owner-with-collaborators',
		defaultMessage:
			'{ownerName}{otherCount, plural, =0 {} one { and 1 other} other { and # others}}',
		description:
			'Page byline showing the owner followed by the number of other human and agent-person contributors.',
	},
	bylineWithoutCount: {
		id: 'content-ownership.byline.owner-with-others',
		defaultMessage: '{ownerName} and others',
		description: 'Page byline showing the owner when there are more than four other contributors.',
	},
});

const styles = cssMap({
	avatarStack: {
		isolation: 'isolate',
	},
	avatar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		flexShrink: 0,
	},
	overlappingAvatar: {
		marginInlineStart: token('space.negative.050'),
	},
});

type CollaboratorsBylineProps = {
	owner: UserShape;
	fallbackContributorCount: number;
};

export const CollaboratorsByline = ({
	owner,
	fallbackContributorCount,
}: CollaboratorsBylineProps): React.JSX.Element => {
	const collaborators = useCollaboratorsQueryResult()?.data?.confluence_contentCollaborators;
	const { formatMessage } = useIntl();
	const colorMode = useAgentAvatarColorMode();
	const items = useMemo(
		() => getCollaboratorItems(collaborators, owner.userId),
		[collaborators, owner.userId],
	);
	const uniqueItems = useMemo(() => {
		const seen = new Set<string>();
		return items.filter((item) => {
			const identity =
				item.kind === 'agent'
					? JSON.stringify(['agent', item.agent.accountId])
					: JSON.stringify(['human', item.user.userId || item.key]);
			if (seen.has(identity)) {
				return false;
			}
			seen.add(identity);
			return true;
		});
	}, [items]);
	// Count each agent-person pair in the text; deduplicate identities only in the avatar stack.
	const otherCount = collaborators?.nodes ? items.length : fallbackContributorCount;
	// The owner is rendered separately and occupies the first slot in the avatar stack.
	const visibleNonOwnerItems = uniqueItems.slice(0, MAX_TOTAL_BYLINE_AVATARS - 1);

	return (
		<Inline space="space.050" alignBlock="center">
			{/* The button's text names the collection; individual avatars are decorative. */}
			<Inline
				alignBlock="center"
				aria-hidden="true"
				testId="byline-collaborator-avatars"
				xcss={styles.avatarStack}
			>
				{/* Paint earlier avatars above later ones, matching AvatarGroup's stack order. */}
				<Box xcss={styles.avatar} style={{ zIndex: visibleNonOwnerItems.length + 1 }}>
					<Avatar
						src={getUserAvatarSrc(owner)}
						borderColor={token('elevation.surface')}
						size="xxsmall"
					/>
				</Box>
				{visibleNonOwnerItems.map((item, index) => (
					<Box
						key={item.key}
						xcss={cx(styles.avatar, styles.overlappingAvatar)}
						style={{ zIndex: visibleNonOwnerItems.length - index }}
					>
						{item.kind === 'agent' ? (
							<AgentRowAvatar
								name={item.agent.displayName}
								// Under branded agent attribution Identity sets the vendor avatar on the
								// agent's own profile, so the brand table is not consulted.
								agentIcon={
									!expValEquals('cc_identity_branded_agent_attribution', 'isEnabled', true)
										? getAgentBrand(item.agent.displayName)?.agentIcon
										: undefined
								}
								avatarUrl={item.agent.avatarUrl}
								avatarSize="xxsmall"
								agentPresenceIconDimensions={
									// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
									fg('agentic_presence_facepile_master_gate')
										? BYLINE_AGENT_PRESENCE_ICON_DIMENSIONS
										: undefined
								}
								hasInlineSpacing={false}
								fillProfileClickableFrame
								presenceOutline={BYLINE_PRESENCE_OUTLINE}
								colorMode={colorMode}
							/>
						) : (
							<Avatar
								src={getUserAvatarSrc(item.user)}
								borderColor={token('elevation.surface')}
								size="xxsmall"
							/>
						)}
					</Box>
				))}
			</Inline>
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{formatMessage(
					otherCount > MAX_NUMBERED_OTHER_CONTRIBUTORS
						? messages.bylineWithoutCount
						: messages.byline,
					{ ownerName: owner.fullName, otherCount },
				)}
			</Text>
		</Inline>
	);
};
